import { type ReactElement } from 'react'
import { isDefined, isNotDefined, type NotificationTopic } from '@terros/common'
import { Button, Stack, Text } from '@mantine/core'
import { useMarkAllNotificationsRead } from '@front/model'

type Props = { topic?: NotificationTopic; disabled?: boolean }

export function MarkAllRead({ topic, disabled }: Props): ReactElement {
  const mutation = useMarkAllNotificationsRead(topic)
  const continuation = mutation.continuation
  async function markAll(): Promise<void> {
    try {
      await mutation.update(continuation ?? { all: true, read: true, topic, before: Date.now() })
    } catch {
      /* The shared retry state preserves the watermark and error across panel closure. */
    }
  }
  return (
    <Stack gap='xs'>
      <Button
        variant='subtle'
        size='xs'
        onClick={markAll}
        loading={mutation.isUpdating}
        disabled={!mutation.ready || (isNotDefined(continuation) && disabled)}
      >
        {isDefined(continuation) && !mutation.isUpdating ? 'Continue marking read' : 'Mark all read'}
      </Button>
      {isDefined(continuation) && !mutation.isUpdating && (
        <Text size='sm' role='status'>
          Some notifications may still be unread. Continue to finish.
        </Text>
      )}
      {isDefined(mutation.error) && (
        <Text size='sm' c='red' role='alert'>
          {mutation.error} Try again.
        </Text>
      )}
    </Stack>
  )
}
